import { IPermissions, Permission, Resource, SystemRole } from '@pepperhq/auth-client';
import { SvgIconComponent } from '@mui/icons-material';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { PEPPER_ORANGE, PEPPER_PURPLE } from 'config/theme';

export interface RoleDisplayConfig {
    displayName: string;
    description: string;
    icon: SvgIconComponent;
    iconColor: string;
}

export const roleDisplay: Partial<Record<SystemRole, RoleDisplayConfig>> = {
    [SystemRole.PepperAdmin]: {
        displayName: 'Pepper Admin',
        description: 'Full access',
        icon: EmojiEventsOutlinedIcon,
        iconColor: PEPPER_PURPLE.core
    },
    [SystemRole.Owner]: {
        displayName: 'Owner',
        description:
            'Full access to all pages and settings. Can manage users, billing, and all merchant configuration.',
        icon: EmojiEventsOutlinedIcon,
        iconColor: PEPPER_ORANGE.secondary
    },
    [SystemRole.Admin]: {
        displayName: 'Administrator',
        description:
            'Broad access similar to Owner but cannot change core merchant settings. Ideal for senior managers.',
        icon: VerifiedUserOutlinedIcon,
        iconColor: PEPPER_PURPLE.secondary
    },
    [SystemRole.SoloMerchantAdmin]: {
        displayName: 'Solo Merchant Admin',
        description:
            'Access to analytics and notifications only. Designed for single-location operators without full console needs.',
        icon: ShieldOutlinedIcon,
        iconColor: '#6B7280'
    },
    [SystemRole.LocationManager]: {
        displayName: 'Location Manager',
        description:
            'Operational access to orders, locations, menus, availability, and pay disputes. No analytics or marketing.',
        icon: ShieldOutlinedIcon,
        iconColor: PEPPER_ORANGE.off
    },
    [SystemRole.TerminalManager]: {
        displayName: 'Quickpad Manager',
        description:
            'Limited access focused on Pepper Pay operations—transactions, blocked lists, disputes, and clerk access.',
        icon: ShieldOutlinedIcon,
        iconColor: '#6B7280'
    }
};

export const fallbackRoleDisplay: RoleDisplayConfig = {
    displayName: 'Custom role',
    description: 'Custom access level.',
    icon: ShieldOutlinedIcon,
    iconColor: '#6B7280'
};

export const getRoleDisplay = (roleId: SystemRole, fallbackName?: string): RoleDisplayConfig => {
    const config = roleDisplay[roleId];
    if (config) {
        return config;
    }
    return {
        ...fallbackRoleDisplay,
        displayName: fallbackName || fallbackRoleDisplay.displayName
    };
};

export interface RolePermissionsItem {
    name: string;
    description?: string;
    permissions: number;
    resource: Resource;
}

export interface RolePermissionsGroup {
    name: string;
    description: string;
    resources: RolePermissionsItem[];
}

export const hasViewAccess = (perms: IPermissions, view: RolePermissionsItem): boolean => {
    if (perms === '*') {
        return true;
    }
    const granted = perms[view.resource] ?? 0;
    // eslint-disable-next-line no-bitwise
    return (granted & view.permissions) === view.permissions;
};

export const PERMISSIONS_VIEW_LIST: RolePermissionsGroup[] = [
    {
        name: 'Locations',
        description: 'Access to location data, state and exports',
        resources: [
            {
                name: 'View locations',
                description: 'See the locations list, individual location details and location tags.',
                permissions: Permission.read,
                resource: Resource.Location
            },
            {
                name: 'Edit locations',
                description: 'Create new locations and update existing location details.',
                permissions: Permission.write,
                resource: Resource.Location
            },
            {
                name: 'Delete locations',
                description: 'Permanently delete or retire locations from the locations table.',
                permissions: Permission.delete,
                resource: Resource.Location
            },
            {
                name: 'Manage location state',
                description: 'Open, close or otherwise change the operational state of a location.',
                permissions: Permission.all,
                resource: Resource.Location
            },
            {
                name: 'Set location busyness level',
                description: 'Adjust the kitchen busyness indicator for each location.',
                permissions: Permission.write,
                resource: Resource.BusynessLevel
            },
            {
                name: 'Export location data',
                description: 'Download location data from the locations table.',
                permissions: Permission.read,
                resource: Resource.LocationExports
            }
        ]
    },
    {
        name: 'Menu',
        description: 'Menu content, customisation and refresh',
        resources: [
            {
                name: 'View menus',
                description: 'See menus, ordering availability, menu enrichments and scheduling.',
                permissions: Permission.read,
                resource: Resource.Menu
            },
            {
                name: 'Edit menus',
                description: 'Update menu content and ordering availability.',
                permissions: Permission.write,
                resource: Resource.Menu
            },
            {
                name: 'Customise menus',
                description: 'Apply per-location menu overrides.',
                permissions: Permission.all,
                resource: Resource.Menu
            },
            {
                name: 'Refresh menus',
                description: 'Trigger a manual menu refresh from the POS.',
                permissions: Permission.execute,
                resource: Resource.MenuRefresh
            }
        ]
    },
    {
        name: 'Orders',
        description: 'Order management and refunds',
        resources: [
            {
                name: 'View orders',
                description: 'See the orders list and individual order details.',
                permissions: Permission.read,
                resource: Resource.Order
            },
            {
                name: 'Force close orders',
                description: 'Manually close orders that are stuck in progress.',
                permissions: Permission.write,
                resource: Resource.OrderForceClose
            },
            {
                name: 'Refund orders',
                description: 'Issue full or partial refunds on completed orders.',
                permissions: Permission.all,
                resource: Resource.OrderRefund
            }
        ]
    },
    {
        name: 'Analytics',
        description: 'Reporting dashboards and reviews',
        resources: [
            {
                name: 'View analytics',
                description:
                    'Access dashboards for orders, customers, loyalty, gifting, audiences, automations and data exports.',
                permissions: Permission.read,
                resource: Resource.Analytics
            },
            {
                name: 'View reviews',
                description: 'See customer review data.',
                permissions: Permission.read,
                resource: Resource.Reviews
            }
        ]
    },
    {
        name: 'Customers',
        description: 'Customer profiles and account controls',
        resources: [
            {
                name: 'View customers',
                description: 'Browse the customer list, view individual profiles and export customer data.',
                permissions: Permission.read,
                resource: Resource.Customer
            },
            {
                name: 'Manage customer accounts',
                description:
                    'Delete customers, invalidate passwords, log out sessions and remove saved payment cards.',
                permissions: Permission.delete,
                resource: Resource.Customer
            }
        ]
    },
    {
        name: 'Mobile content',
        description: 'Mobile app layouts and modules',
        resources: [
            {
                name: 'View and edit mobile content',
                description: 'Access and modify mobile app layouts, modules and templates.',
                permissions: Permission.read,
                resource: Resource.Layout
            }
        ]
    },
    {
        name: 'Loyalty',
        description: 'Perks, vouchers and loyalty schemes',
        resources: [
            {
                name: 'View perks',
                description: 'Access Pepper and 3rd-party perks.',
                permissions: Permission.read,
                resource: Resource.Perk
            },
            {
                name: 'Reset perks',
                description: 'Use the "Remove all awards" action to reset perk progress.',
                permissions: Permission.all,
                resource: Resource.PerkReset
            },
            {
                name: 'View vouchers',
                description: 'See the voucher list and individual voucher details.',
                permissions: Permission.read,
                resource: Resource.Voucher
            },
            {
                name: 'View adjustment schemes',
                description: 'Access Eagle Eye coupon adjustment schemes.',
                permissions: Permission.read,
                resource: Resource.AdjustmentSchemes
            },
            {
                name: 'View birthday loyalty',
                description: 'Access birthday loyalty schemes.',
                permissions: Permission.read,
                resource: Resource.BirthdayLoyaltySchemes
            },
            {
                name: 'View tiered loyalty',
                description: 'Access tiered loyalty schemes.',
                permissions: Permission.read,
                resource: Resource.TieredLoyaltySchemes
            }
        ]
    },
    {
        name: 'Settings',
        description: 'Merchant configuration and user access',
        resources: [
            {
                name: 'Manage user access',
                description: 'Invite users and manage their access levels on the Access Management page.',
                permissions: Permission.write,
                resource: Resource.TenantAccess
            },
            {
                name: 'Edit public settings',
                description:
                    'View and edit merchant settings: global, branding, ordering, payments, marketing, POS and more.',
                permissions: Permission.write,
                resource: Resource.Setting
            },
            {
                name: 'Edit private settings',
                description: 'View and edit sensitive merchant settings alongside public ones.',
                permissions: Permission.write,
                resource: Resource.Secret
            }
        ]
    },
    {
        name: 'Notifications',
        description: 'Customer notifications and campaigns',
        resources: [
            {
                name: 'View notifications and campaigns',
                description:
                    'Access notifications, scheduled and triggered campaigns, and individual notification details.',
                permissions: Permission.read,
                resource: Resource.Message
            }
        ]
    },
    {
        name: 'Pepper Pay',
        description: 'Pepper Pay transactions, disputes and reporting',
        resources: [
            {
                name: 'Access Pepper Pay',
                description:
                    'View transactions, blocked lists, disputes and individual transaction or dispute details.',
                permissions: Permission.read,
                resource: Resource.Pay
            },
            {
                name: 'View Pepper Pay reporting',
                description: 'Access the Pepper Pay reporting page.',
                permissions: Permission.read,
                resource: Resource.PayReporting
            }
        ]
    },
    {
        name: 'Terminal',
        description: 'Terminal access and clerk management',
        resources: [
            {
                name: 'View terminal access',
                description: 'Open the Terminal Access page.',
                permissions: Permission.read,
                resource: Resource.TerminalAccess
            },
            {
                name: 'Manage clerks',
                description: 'Add, edit and remove terminal clerks.',
                permissions: Permission.read + Permission.write + Permission.delete,
                resource: Resource.TerminalAccess
            }
        ]
    }
];

export const TOTAL_VIEWS_COUNT = PERMISSIONS_VIEW_LIST.reduce(
    (acc, group) => acc + group.resources.length,
    0
);

export const countAccessibleViews = (perms: IPermissions): number =>
    PERMISSIONS_VIEW_LIST.reduce(
        (acc, group) => acc + group.resources.filter(view => hasViewAccess(perms, view)).length,
        0
    );
